// ./src/utils/api/CarMonthlySummaryService.ts
import { gql } from "@apollo/client";
import { ApiService } from "./ApiService";
import { OpResult } from "@sdflc/api-helpers";
import { Pagination, SortItem } from "./commonApiTypes";
import { Car } from "./CarService";

// Fragment for MonthlySummaryUnits to avoid repetition
const MONTHLY_SUMMARY_UNITS_FRAGMENT = gql`
  fragment MonthlySummaryUnitsFields on MonthlySummaryUnits {
    distanceUnit
    volumeUnit
    startMileage
    endMileage
    travelsDistance
    refuelsVolume
    consumptionVolume
    consumptionUnit
    consumption
  }
`;

// GraphQL queries
const CAR_MONTHLY_SUMMARY_LIST_QUERY = gql`
  ${MONTHLY_SUMMARY_UNITS_FRAGMENT}
  query CarMonthlySummaryList(
    $filter: CarMonthlySummaryFilter
    $params: PaginationAndSorting
  ) {
    carMonthlySummaryList(filter: $filter, params: $params) {
      code
      data {
        id
        carId
        homeCurrency
        year
        month
        startMileage
        endMileage
        refuelsCount
        expensesCount
        refuelsTaxes
        refuelsCost
        expensesFees
        expensesTaxes
        expensesCost
        refuelsVolume
        revenuesCount
        revenuesAmount
        maintenanceCount
        maintenanceCost
        consumptionVolume
        isFirstRefuelMonth
        checkpointsCount
        travelsCount
        travelsDistance
        firstRecordAt
        lastRecordAt
        updatedAt
        inCarUnits {
          ...MonthlySummaryUnitsFields
        }
        inUserUnits {
          ...MonthlySummaryUnitsFields
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const CAR_MONTHLY_SUMMARY_GET_QUERY = gql`
  ${MONTHLY_SUMMARY_UNITS_FRAGMENT}
  query CarMonthlySummaryGet($id: ID) {
    carMonthlySummaryGet(id: $id) {
      code
      data {
        id
        carId
        homeCurrency
        year
        month
        startMileage
        endMileage
        refuelsCount
        expensesCount
        refuelsTaxes
        refuelsCost
        expensesFees
        expensesTaxes
        expensesCost
        refuelsVolume
        revenuesCount
        revenuesAmount
        maintenanceCount
        maintenanceCost
        consumptionVolume
        isFirstRefuelMonth
        checkpointsCount
        travelsCount
        travelsDistance
        firstRecordAt
        lastRecordAt
        updatedAt
        inCarUnits {
          ...MonthlySummaryUnitsFields
        }
        inUserUnits {
          ...MonthlySummaryUnitsFields
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const CAR_MONTHLY_SUMMARY_GET_MANY_QUERY = gql`
  ${MONTHLY_SUMMARY_UNITS_FRAGMENT}
  query CarMonthlySummaryGetMany($ids: [ID]) {
    carMonthlySummaryGetMany(ids: $ids) {
      code
      data {
        id
        carId
        homeCurrency
        year
        month
        startMileage
        endMileage
        refuelsCount
        expensesCount
        refuelsTaxes
        refuelsCost
        expensesFees
        expensesTaxes
        expensesCost
        refuelsVolume
        revenuesCount
        revenuesAmount
        maintenanceCount
        maintenanceCost
        consumptionVolume
        isFirstRefuelMonth
        checkpointsCount
        travelsCount
        travelsDistance
        firstRecordAt
        lastRecordAt
        updatedAt
        car {
          id
          label
          make
          model
          color
        }
        inCarUnits {
          ...MonthlySummaryUnitsFields
        }
        inUserUnits {
          ...MonthlySummaryUnitsFields
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// Types

/**
 * Converted values with explicit units for distance and volume fields.
 * Used for both car-specific units and user preference units.
 */
export interface MonthlySummaryUnits {
  /** Distance unit: km, mi */
  distanceUnit: string;
  /** Volume unit: l, gal-us, gal-uk */
  volumeUnit: string;
  /** Mileage at start of month in specified distance unit */
  startMileage?: number | undefined;
  /** Mileage at end of month in specified distance unit */
  endMileage?: number | undefined;
  /** Total distance from completed travels in specified distance unit */
  travelsDistance?: number | undefined;
  /** Total fuel volume purchased in specified volume unit */
  refuelsVolume?: number | undefined;
  /** Fuel volume for consumption calculation in specified volume unit */
  consumptionVolume?: number | undefined;
  /** Consumption unit: l100km, km-l, mpg-us, mpg-uk, etc */
  consumptionUnit?: string | undefined
  /** Calculated fuel consumption in specified consumption unit */
  consumption?: number | undefined
}

export interface CarMonthlySummary {
  id?: string | undefined;
  carId?: string | undefined;
  homeCurrency?: string | undefined;
  year?: number | undefined;
  month?: number | undefined;
  /** Mileage at start of month (metric: km) */
  startMileage?: number | undefined;
  /** Mileage at end of month (metric: km) */
  endMileage?: number | undefined;
  refuelsCount?: number | undefined;
  expensesCount?: number | undefined;
  refuelsTaxes?: number | undefined;
  refuelsCost?: number | undefined;
  expensesFees?: number | undefined;
  expensesTaxes?: number | undefined;
  expensesCost?: number | undefined;
  /** Total fuel volume purchased (metric: liters) */
  refuelsVolume?: number | undefined;
  // Revenue tracking
  revenuesCount?: number | undefined;
  revenuesAmount?: number | undefined;
  // Maintenance tracking (expenses where is_it_maintenance = true)
  maintenanceCount?: number | undefined;
  maintenanceCost?: number | undefined;
  /** Fuel volume for consumption calc (metric: liters) */
  consumptionVolume?: number | undefined;
  isFirstRefuelMonth?: boolean | undefined;
  // Checkpoint tracking
  checkpointsCount?: number | undefined;
  // Travel tracking
  travelsCount?: number | undefined;
  /** Total distance from completed travels (metric: km) */
  travelsDistance?: number | undefined;
  // Timestamps
  firstRecordAt?: string | undefined;
  lastRecordAt?: string | undefined;
  updatedAt?: string | undefined;
  car?: Car | undefined;
  /** Values converted to car's preferred units (mileageIn, mainTankVolumeEnteredIn) */
  inCarUnits?: MonthlySummaryUnits | undefined;
  /** Values converted to user's preferred units (distanceIn, volumeIn from profile) */
  inUserUnits?: MonthlySummaryUnits | undefined;
}

export interface CarMonthlySummaryFilter {
  id?: string[] | null | undefined;
  carId?: string[] | null | undefined;
  homeCurrency?: string[] | null | undefined;
  year?: number[] | null | undefined;
  month?: number[] | null | undefined;
}

/**
 * CarMonthlySummary service that handles car monthly summary-related API calls.
 * This is a read-only service for viewing aggregated monthly statistics per car.
 * Supports: list, get, getMany
 */
export class CarMonthlySummaryService {
  /**
   * Get a list of car monthly summaries with optional filtering, pagination, and sorting
   */
  static async list(
    filter?: CarMonthlySummaryFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    const result = await ApiService.executeQuery(
      CAR_MONTHLY_SUMMARY_LIST_QUERY,
      {
        filter: filter || {},
        params: {
          pagination: pagination || undefined,
          sortBy: sortBy || undefined,
        },
      }
    );
    return result;
  }

  /**
   * Get a single car monthly summary by ID
   */
  static async get(id: string): Promise<OpResult> {
    const result = await ApiService.executeQuery(
      CAR_MONTHLY_SUMMARY_GET_QUERY,
      {
        id,
      }
    );
    return result;
  }

  /**
   * Get multiple car monthly summaries by IDs
   */
  static async getMany(ids: string[]): Promise<OpResult> {
    const result = await ApiService.executeQuery(
      CAR_MONTHLY_SUMMARY_GET_MANY_QUERY,
      {
        ids,
      }
    );
    return result;
  }
}